import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Mail } from 'lucide-react';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Scroll to top when component mounts or product changes
    window.scrollTo(0, 0);
    
    const fetchProduct = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        console.log('Fetching product from:', `${backendUrl}/api/products/${id}`);
        
        const response = await fetch(`${backendUrl}/api/products/${id}`);
        console.log('Response status:', response.status);
        
        if (response.ok) {
          const productData = await response.json();
          console.log('Product data received:', productData);
          setProduct(productData);
        } else {
          console.error('Product not found, status:', response.status);
          setProduct(null);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching product:', error);
        setProduct(null);
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const nextImage = () => {
    if (product && product.images) {
      setCurrentImageIndex((prev) => 
        prev === product.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (product && product.images) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? product.images.length - 1 : prev - 1
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">Loading...</div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-automats-black mb-4">Product Not Found</h1>
            <a 
              href="/products"
              className="text-automats-red hover:underline"
            >
              Back to Products
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <a 
            href="/products"
            className="flex items-center text-automats-red hover:underline text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Products
          </a>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="relative bg-gray-100 rounded-2xl overflow-hidden aspect-square">
              {product.images && product.images.length > 0 && (
                <img 
                  src={product.images[currentImageIndex]}
                  alt={`${product.name} - Image ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover"
                />
              )}
              
              {/* Image Navigation */}
              {product.images && product.images.length > 1 && (
                <>
                  <button 
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-automats-black p-2 rounded-full transition-all"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <button 
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-automats-black p-2 rounded-full transition-all"
                  >
                    <ArrowRight className="h-5 w-5" />
                  </button>
                </>
              )}

              {/* Image Counter */}
              {product.images && product.images.length > 1 && (
                <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                  {currentImageIndex + 1} / {product.images.length}
                </div>
              )}
            </div>

            {/* Thumbnail Images */}
            {product.images && product.images.length > 1 && (
              <div className="flex gap-2">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-1 aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      currentImageIndex === index 
                        ? 'border-automats-red' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img 
                      src={image}
                      alt={`${product.name} - Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover bg-gray-100"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Information */}
          <div className="space-y-8">
            {/* Header */}
            <div>
              <div className="text-sm text-automats-red font-medium mb-2">
                {product.category}
              </div>
              <h1 className="text-4xl font-semibold text-automats-black mb-4">
                {product.name}
              </h1>
              <p className="text-xl text-automats-black leading-relaxed">
                {product.description}
              </p>
            </div>

            {/* Minimum Order */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="text-lg font-semibold text-automats-black mb-2">
                Minimum Order Quantity
              </div>
              <div className="text-2xl font-semibold text-automats-red">
                {product.min_order} sets
              </div>
            </div>

            {/* Features */}
            {product.features && product.features.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold text-automats-black mb-4">Key Features</h3>
                <ul className="space-y-2">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <span className="w-2 h-2 rounded-full bg-automats-red mt-2 mr-3 flex-shrink-0"></span>
                      <span className="text-automats-black">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Specifications */}
            {product.specifications && (
              <div>
                <h3 className="text-xl font-semibold text-automats-black mb-4">Specifications</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key}>
                      <div className="text-automats-steel font-medium capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </div>
                      <div className="text-automats-black">{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Colors */}
            {product.colors && product.colors.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold text-automats-black mb-4">Available Colors</h3>
                <div className="flex gap-2">
                  {product.colors.map((color, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-automats-black rounded-md text-sm"
                    >
                      {color}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="pt-4">
              <a 
                href={`mailto:sales@automats.us?subject=Quote Request - ${product.name}&body=Hello, I would like to request a quote for the ${product.name}.`}
                className="btn-clean btn-premium text-white text-lg font-medium w-full"
              >
                <Mail className="mr-2 h-5 w-5" />
                Request Quote
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;